import { createUseStyles } from 'react-jss';

import { FontWeight } from '../../../styles/fonts';
import { canHover, textOverflowHidden } from '../../../styles/helpers';
import { Transition } from '../../../styles/enums';
import { ITheme } from '../../../styles/theme';

import IButton from './types';

const useStyles = createUseStyles<
  string,
  { variant: IButton['variant']; size: IButton['size']; selected: boolean }
>(
  ({ button, spacing }: ITheme) => ({
    root: {
      position: 'relative',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',

      padding: ({ variant, size }) => {
        if (variant === 'icon') {
          return '0px';
        }
        if (variant === 'quaternary') return `6px 12px`;

        if (variant === 'quinary') return `4px 12px`;

        return size === 'large' ? `14px 16px` : `4px 16px`;
      },
      border: ({ variant, selected }) => {
        if (variant === 'icon' || variant === 'text') {
          return 'none';
        }
        if (variant == 'quaternary') {
          if (selected) {
            return `1px solid ${button[variant].borderHover}`;
          }
          return `1px solid ${button[variant].border}`;
        }

        if (variant === 'quinary') return `0px solid ${button[variant].borderHover}`;

        return `2px solid ${button[variant].border}`;
      },
      borderRadius: ({ variant }) => {
        if (variant === 'icon') return 0;
        if (variant === 'quaternary') return 50;
        if (variant === 'quinary') return 4;
        return 8;
      },

      transition: `background-color ${Transition.Default} ease-in-out`,
      cursor: 'pointer',

      background: ({ variant, selected }) => {
        if (variant == 'quaternary' && selected) {
          return button[variant].backgroundHover;
        }

        if (variant == 'quinary' && selected) {
          return button[variant].backgroundHover;
        }

        return button[variant].background;
      },
      color: ({ variant, selected }) => {
        if (variant == 'quaternary' && selected) {
          return button[variant].colorHover;
        }

        if (variant == 'quinary' && selected) {
          return button[variant].colorHover;
        }

        return button[variant].color;
      },

      '&[disabled]': {
        opacity: '0.5',
        cursor: 'default',

        '&:hover': {
          backgroundColor: ({ variant }) => button[variant].background,
        },

        '& > $content': {
          color: ({ variant }) => button[variant].color,
        },
      },

      [canHover()]: {
        '&:hover': {
          backgroundColor: ({ variant }) => button[variant].backgroundHover,
          border: ({ variant }) => {
            if (variant === 'icon') {
              return 'none';
            }
            if (variant == 'quaternary') {
              return `1px solid ${button[variant].borderHover}`;
            }

            if (variant == 'quinary') {
              return `0px solid ${button[variant].borderHover}`;
            }

            return `2px solid ${button[variant].borderHover}`;
          },

          '& svg path': {
            fill: ({ variant }) => (variant === 'icon' ? button[variant].colorHover : 'auto'),
          },

          '& > $content': {
            color: ({ variant }) => button[variant].colorHover,
          },

          '&[disabled]': {
            '& > $content': {
              color: ({ variant }) => button[variant].color,
            },
            border: ({ variant }) => {
              if (variant === 'icon' || variant === 'text') {
                return 'none';
              }
              if (variant == 'quaternary') {
                return `1px solid ${button[variant].border}`;
              }

              if (variant == 'quinary') {
                return `1px solid ${button[variant].border}`;
              }

              return `2px solid ${button[variant].border}`;
            },
          },
        },
      },
      [canHover(false)]: {
        '&:active': {
          backgroundColor: ({ variant }) => button[variant].backgroundHover,

          '& > $content': {
            color: ({ variant }) => button[variant].colorHover,
          },
        },
      },
    },
    content: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing.small,

      fontWeight: FontWeight.SemiBold,
      fontSize: ({ variant }) => (variant === 'quaternary' || variant == 'quinary' ? 14 : 16),

      color: ({ variant, selected }) => {
        if (variant == 'quaternary' && selected) {
          return button[variant].colorHover;
        }

        if (variant == 'quinary' && selected) {
          return button[variant].colorHover;
        }

        return button[variant].color;
      },

      lineHeight: '20px',
      transition: ({ variant }) =>
        variant === 'icon' ? 'unset' : `color ${Transition.Default} ease-in-out`,

      ...textOverflowHidden,

      '& svg': {
        '& > path': {
          fill: 'currentColor',
        },
      },
    },
  }),
  {
    name: 'button',
    link: true,
  },
);

export default useStyles;
